<template>
  <Component
    :is="c_tag"
    :prefetch="prefetch"
    class="buttonio"
    :class="[
      ...c_dynamicSize,
      `buttonio-${color}`,
      {
        'buttonio-small-mobile': sizeSmallMobile,
        'buttonio-small': sizeSmall || sizeSmallMobile,
        'icon-only': c_iconOnly,
        'icon-left': iconLeft || icon && !c_iconOnly && !iconRight,
        'icon-right': iconRight,
        'signal': signal,
        'active': active,
        'full-width': fullWidth,
      },
    ]"
    :type="c_tag === 'button' ? c_type : undefined"
    v-bind="{...c_link, ...$attrs}"
    :disabled="disabled || loading"
    :rel="noReffer ? 'noopener noreferrer': undefined"
    @click="$emit('click', $event)"
    @mouseover="$emit('mouseover')"
    @mouseleave="$emit('mouseleave')"
  >
    <img
      v-if="iconSrc"
      :src="iconSrc"
      alt="icon"
      class="buttonio__svg"
    />
    <SvgIcon
      v-else-if="icon"
      :name="icon"
      class="buttonio__svg"
    />
    <img
      v-if="loading"
      src="/img/loaders/btn-loader.svg"
      alt="icon"
      class="buttonio__image"
    />
    <slot>{{ text }}</slot>
  </Component>
</template>

<script>
import { createBreakpointValidator } from '@/util/helpers/createBreakpointValidator'
import { trimLastSlash } from '~/util/helpers/string'

const COLORS = {
  DEFAULT: 'default', // gray
  GRAY: 'gray',
  PRIMARY: 'primary', // purple
  SECONDARY: 'secondary', // yellow
  TRANSPARENT: 'transparent',
  WHITE: 'white',
  LINE: 'line', // bordered
  INFO: 'info', // bordered
  WARNING: 'warning', // bordered
  ERROR: 'error', // bordered
}
const SIZES = [
  'xs',
  's',
  'm',
  'l', //h-56
]

export const colorValidator = (value) => Object.values(COLORS).includes(value)
const breakpointValidator = createBreakpointValidator(SIZES)

export default {
  name: 'UiBtn',
  props: {
    prefetch: { type: Boolean, default: false },
    disabled: { type: Boolean },
    active: { type: Boolean },
    submit: { type: Boolean },
    loading: { type: Boolean },
    text: { type: String },
    iconOnly: { type: Boolean },
    iconLeft: { type: Boolean },
    iconRight: { type: Boolean },
    icon: { type: String },
    iconSrc: { type: String },
    to: [Object, String],
    href: { type: String },
    signal: { type: Boolean },
    noReffer: { type: Boolean },
    /** style */
    sizeSmallMobile: { type: Boolean }, // 32 --> 44
    sizeSmall: { type: Boolean }, // 32
    color: { type: String, validator: colorValidator, default: COLORS.DEFAULT },
    size: { type: [String, Object], validator: breakpointValidator, default: 'm' },
    modalTo: { type: [String, Object] },
    tag: { type: String },
    fullWidth: { type: Boolean },
  },
  computed: {
    c_link() {
      const link = {}
      if (this.to) {
        link.to = this.c_to
      }
      if (this.href) {
        link.href = this.href
      }
      if (this.modalTo) {
        link.to = this.modalTo
      }
      return link
    },
    c_to() {
      if (typeof this.to === 'string') {
        return trimLastSlash(this.to)
      }
      return this.to
    },
    c_type() {
      return this.submit ? 'submit' : 'button'
    },
    c_tag() {
      return this.tag ||  (this.to ? 'NuxtLink' : this.href ? 'a' : this.modalTo ? 'ModalLink' : 'button')
    },
    c_iconOnly() {
      return this.iconOnly || !(this.text || this.$slots.default)
    },
    c_dynamicSize() {
      // If size is a string ("xs" | "s" | "m" | "l"),
      // we just return ['size-something'].
      if (typeof this.size === 'string') {
        return [`size-${this.size}`]
      }

      // Otherwise, size is an object:
      // e.g. { default: 's', desktop-mid: 'm', ... }
      const classes = []
      // Always add the default size class
      if (this.size.default) {
        classes.push(`size-${this.size.default}`)
      }

      // For each breakpoint key (like 'desktop-mid'),
      // create a special "responsive-size-{breakpoint}-{value}" class
      for (const [bp, val] of Object.entries(this.size)) {
        if (bp === 'default') continue
        classes.push(`responsive-size-${bp}-${val}`)
      }

      return classes
    },
  },
}
</script>

<style src="./style.scss" lang="scss" />
